/**
 * Print-friendly Report View
 * Optimized layout for printing tax reports
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PrintReport = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const taxYear = searchParams.get('tax_year');
  const [stats, setStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const printRef = useRef();

  // Memoize expensive aggregations
  const tripTotals = useMemo(() => ({
    qualifyingDays: trips.reduce((sum, t) => sum + (t.qualifying_days || 0), 0),
    totalEarnings: trips.reduce((sum, t) => sum + (t.total_earnings || 0), 0),
  }), [trips]);

  const expenseTotals = useMemo(() => ({
    total: expenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    taxDeductible: expenses.filter(e => e.is_tax_deductible).reduce((sum, e) => sum + (e.amount || 0), 0),
  }), [expenses]);

  const fetchData = useCallback(async () => {
    if (!taxYear) {
      toast.error('No tax year specified');
      navigate('/reports');
      return;
    }

    setLoading(true);
    try {
      const [statsRes, tripsRes, expensesRes, docsRes, userRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats?tax_year=${taxYear}`),
        axios.get(`${API_URL}/api/trips?tax_year=${taxYear}`),
        axios.get(`${API_URL}/api/expenses?tax_year=${taxYear}`),
        axios.get(`${API_URL}/api/documents?tax_year=${taxYear}`),
        axios.get(`${API_URL}/api/auth/me`)
      ]);
      
      setStats(statsRes.data);
      setTrips(tripsRes.data.trips || []);
      setExpenses(expensesRes.data.expenses || []);
      setDocuments(docsRes.data.documents || []);
      setUser(userRes.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [taxYear, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    window.print();
  };

  const tripTypeLabels = {
    work_trip: 'At Sea/On Vessel',
    qualifying_holiday: 'Qualifying Holiday',
    hotel_stay: 'Hotel Stay (Abroad)',
    uk_hotel_stay: 'Hotel Stay (UK)',
    course_uk: 'Course (UK)',
    course_foreign: 'Course (Abroad)',
    paid_leave: 'Paid Leave',
    wage_adjustment: 'Wage Adjustment',
    base_work_uk: 'Base Work (UK)',
    base_work_foreign: 'Base Work (Abroad)'
  };

  const categoryLabels = {
    travel: 'Travel',
    accommodation: 'Accommodation',
    equipment: 'Equipment',
    training: 'Training/Certification',
    medical: 'Medical',
    visa: 'Visa/Documentation',
    communication: 'Communication',
    subsistence: 'Subsistence/Meals',
    other: 'Other'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (err) {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden fixed top-0 left-0 right-0 bg-slate-900 border-b border-slate-700 p-4 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/reports')}
            className="text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-slate-400 text-sm">Tax Year: {taxYear}</span>
            <Button 
              onClick={handlePrint}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Report
            </Button>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div ref={printRef} className="print-content bg-white min-h-screen pt-20 print:pt-0">
        <div className="max-w-4xl mx-auto p-8 print:p-6">
          
          {/* Header */}
          <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Seafarer's Earnings Deduction
            </h1>
            <h2 className="text-xl text-gray-700 mb-4">Tax Summary Report</h2>
            <div className="flex justify-center gap-8 text-sm text-gray-600">
              <span><strong>Tax Year:</strong> {taxYear}</span>
              <span><strong>Generated:</strong> {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
          </div>

          {/* Taxpayer Info */}
          {user && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg print:bg-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Taxpayer Information</h3>
              <p className="text-gray-900"><strong>Name:</strong> {user.name}</p>
              <p className="text-gray-900"><strong>Email:</strong> {user.email}</p>
            </div>
          )}

          {/* Summary Section */}
          {stats && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                TAX YEAR SUMMARY
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 border border-gray-200 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">{stats.starting_balance}</p>
                  <p className="text-xs text-gray-500 uppercase">Carry Forward</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg text-center bg-amber-50">
                  <p className="text-2xl font-bold text-amber-700">{stats.qualifying_days}</p>
                  <p className="text-xs text-gray-500 uppercase">Qualifying Days</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">{stats.days_in_hand}</p>
                  <p className="text-xs text-gray-500 uppercase">Days in Hand</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">£{stats.total_earnings?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 uppercase">Total Earnings</p>
                </div>
              </div>

              {/* SED Status */}
              <div className={`p-4 rounded-lg mb-4 ${
                stats.claim_broken 
                  ? 'bg-red-100 border border-red-300' 
                  : stats.sed_eligible 
                    ? 'bg-amber-100 border border-amber-300' 
                    : 'bg-amber-100 border border-amber-300'
              }`}>
                <p className={`font-bold ${
                  stats.claim_broken ? 'text-red-800' : stats.sed_eligible ? 'text-amber-800' : 'text-amber-800'
                }`}>
                  SED CLAIM STATUS: {
                    stats.claim_broken 
                      ? 'BROKEN (183+ consecutive UK days)' 
                      : stats.sed_eligible 
                        ? 'ELIGIBLE' 
                        : 'BUILDING ELIGIBILITY'
                  }
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <p><strong>Number of Trips:</strong> {stats.trips_count}</p>
                <p><strong>Max Consecutive UK Days:</strong> {stats.max_consecutive_uk_days}</p>
              </div>
            </div>
          )}

          {/* Trips Table */}
          {trips.length > 0 && (
            <div className="mb-8 page-break-inside-avoid">
              <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                TRIP DETAILS ({trips.length} trips)
              </h3>
              
              <table className="w-full text-sm border-collapse text-gray-900">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-2 text-left text-gray-900">#</th>
                    <th className="border border-gray-300 px-2 py-2 text-left text-gray-900">Type</th>
                    <th className="border border-gray-300 px-2 py-2 text-left text-gray-900">Dates</th>
                    <th className="border border-gray-300 px-2 py-2 text-left text-gray-900">Vessel/Location</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-gray-900">Qual. Days</th>
                    <th className="border border-gray-300 px-2 py-2 text-right text-gray-900">Earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map((trip, idx) => (
                    <tr key={trip.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-2 py-1 text-gray-800">{idx + 1}</td>
                      <td className="border border-gray-300 px-2 py-1 text-gray-800">
                        {tripTypeLabels[trip.trip_type] || trip.trip_type}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-xs text-gray-800">
                        {formatDate(trip.date_left_uk || trip.date_joined_vessel)} - {formatDate(trip.date_returned_uk || trip.date_left_vessel)}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-gray-800">
                        {trip.vessel_name || trip.hotel_name || trip.base_name || trip.course_name || '-'}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-center text-gray-800">{trip.qualifying_days || 0}</td>
                      <td className="border border-gray-300 px-2 py-1 text-right text-gray-800">£{(trip.total_earnings || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="4" className="border border-gray-300 px-2 py-2 text-right text-gray-900">TOTALS:</td>
                    <td className="border border-gray-300 px-2 py-2 text-center text-gray-900">
                      {tripTotals.qualifyingDays}
                    </td>
                    <td className="border border-gray-300 px-2 py-2 text-right text-gray-900">
                      £{tripTotals.totalEarnings.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Expenses Table */}
          {expenses.length > 0 && (
            <div className="mb-8 page-break-inside-avoid">
              <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                EXPENSES SUMMARY ({expenses.length} items)
              </h3>
              
              <table className="w-full text-sm border-collapse text-gray-900">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-2 text-left text-gray-900">Date</th>
                    <th className="border border-gray-300 px-2 py-2 text-left text-gray-900">Category</th>
                    <th className="border border-gray-300 px-2 py-2 text-left text-gray-900">Description</th>
                    <th className="border border-gray-300 px-2 py-2 text-center text-gray-900">Tax Ded.</th>
                    <th className="border border-gray-300 px-2 py-2 text-right text-gray-900">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense, idx) => (
                    <tr key={expense.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-2 py-1 text-gray-800">{formatDate(expense.date)}</td>
                      <td className="border border-gray-300 px-2 py-1 text-gray-800">{categoryLabels[expense.category] || expense.category}</td>
                      <td className="border border-gray-300 px-2 py-1 text-gray-800">{expense.description}</td>
                      <td className="border border-gray-300 px-2 py-1 text-center text-gray-800">{expense.is_tax_deductible ? '✓' : '-'}</td>
                      <td className="border border-gray-300 px-2 py-1 text-right text-gray-800">£{(expense.amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="4" className="border border-gray-300 px-2 py-2 text-right text-gray-900">TOTAL:</td>
                    <td className="border border-gray-300 px-2 py-2 text-right text-gray-900">
                      £{expenseTotals.total.toLocaleString()}
                    </td>
                  </tr>
                  <tr className="bg-amber-50 font-bold">
                    <td colSpan="4" className="border border-gray-300 px-2 py-2 text-right text-gray-900">TAX DEDUCTIBLE:</td>
                    <td className="border border-gray-300 px-2 py-2 text-right text-amber-700">
                      £{expenseTotals.taxDeductible.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Documents List */}
          {documents.length > 0 && (
            <div className="mb-8 page-break-inside-avoid">
              <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                SUPPORTING DOCUMENTS ({documents.length} files)
              </h3>
              
              <ul className="list-disc list-inside text-sm space-y-1">
                {documents.map((doc, idx) => (
                  <li key={doc.id || idx} className="text-gray-700">
                    <strong>{doc.file_name}</strong> - {doc.document_type?.replace('_', ' ')}
                    {doc.month && <span className="text-gray-500"> ({doc.month})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-8 pt-6 border-t-2 border-gray-300 text-sm text-gray-600 page-break-inside-avoid">
            <h3 className="font-bold text-gray-900 mb-3">IMPORTANT NOTES & DISCLAIMER</h3>
            <p className="mb-3">
              This report has been generated by Seafarer Logbook for the purpose of tracking Seafarer's Earnings Deduction eligibility.
            </p>
            <p className="mb-3"><strong>Key Points for SED Claims:</strong></p>
            <ul className="list-disc list-inside mb-3 space-y-1">
              <li>You must work outside the UK for 183 days or more in a 365-day period</li>
              <li>Your duties must be performed wholly outside the UK</li>
              <li>You must not spend more than 183 consecutive days in the UK</li>
              <li>Qualifying days are counted using the 'half-day' rule for departure and return days</li>
            </ul>
            <p className="mb-3">
              <strong>DISCLAIMER:</strong> This report is for informational purposes only and should not be considered as professional tax advice. 
              Please consult with a qualified tax professional, accountant, or HMRC directly for official guidance on Seafarer's Earnings Deduction claims.
            </p>
            <p className="text-xs">Reference: HMRC IR 205(S) - Seafarer's Earnings Deduction</p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500 text-center">
            <p>Report generated by Seafarer Logbook on {new Date().toLocaleString('en-GB')}</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print-content {
            background: white !important;
          }
          
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
          
          table {
            page-break-inside: auto;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          thead {
            display: table-header-group;
          }
          
          tfoot {
            display: table-footer-group;
          }
        }
      `}</style>
    </>
  );
};

export default PrintReport;
